import React from 'react';
import { Link } from 'react-router-dom';
import { Crown } from 'lucide-react';
import Avatar from '../avatar/avatar.component';
import { EmptyComponent } from '../empty/empty-component';
import MemberMark from '../membership/member-mark';
import { maskEmailLike } from '../../utils/honor-rows';

// Xếp hạng: gradient huy chương cho top 1-3, số thứ tự cho phần còn lại.
const rankStyle = (rank: number) => {
  if (rank === 1)
    return 'bg-gradient-to-br from-amber-300 to-amber-500 text-white ring-amber-300/50';
  if (rank === 2)
    return 'bg-gradient-to-br from-slate-300 to-slate-400 text-white ring-slate-300/50';
  if (rank === 3)
    return 'bg-gradient-to-br from-orange-300 to-orange-500 text-white ring-orange-300/50';
  return 'bg-slate-100 text-slate-500 ring-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-600';
};

// Tên hiển thị dòng chính: fullName (trim), rỗng thì name, rồi userName.
// Nguồn /vinh-danh (followers/donator/receives/interactions) có fullName; nguồn
// creator-top-donators (trang cá nhân, topMoney/topAction) chỉ có name/userName.
const displayName = (item: any): string => {
  const trimmedFullName =
    typeof item?.fullName === 'string' ? item.fullName.trim() : '';
  return maskEmailLike(trimmedFullName || item?.name || item?.userName || '');
};

// Giữ nguyên đơn vị như bản cũ để không đổi ý nghĩa dữ liệu; thêm nhánh score
// (tab "Tương tác" của khung trang chủ, đơn vị "điểm").
const formatValue = (item: any) => {
  if (item?.totalAmount)
    return {
      value: `${item.totalAmount}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      unit: 'Ca',
    };
  if (item?.totalFollow)
    return { value: `${item.totalFollow}`, unit: 'người theo dõi' };
  if (item?.score !== undefined && item?.score !== null)
    return {
      value: `${item.score}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      unit: 'điểm',
    };
  return { value: `${item?.totalAction ?? 0}`, unit: 'Tương tác' };
};

/** memberCreatorId: có truyền thì hiện huy hiệu hội viên của kênh đó cạnh tên (bảng xếp hạng trên trang streamer). */
const UkSlider = ({ sliderItems, memberCreatorId }: any) => {
  if (!sliderItems || sliderItems.length === 0) return <EmptyComponent />;

  return (
    <ul className="flex flex-col">
      {sliderItems.map((item: any, index: number) => {
        const rank = index + 1;
        const { value, unit } = formatValue(item);
        const name = displayName(item);
        const rowClassName =
          'group flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/60';
        const rowContent = (
          <>
              {/* Rank */}
              <div
                className={`relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ring-1 ${rankStyle(
                  rank
                )}`}
              >
                {rank === 1 ? <Crown size={14} fill="currentColor" /> : rank}
              </div>

              {/* Avatar */}
              <div className="shrink-0">
                <Avatar
                  avatar={item?.avatar}
                  label={name || item?.userName}
                  size="sm"
                />
              </div>

              {/* Name */}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-800 group-hover:text-primary dark:text-slate-100">
                  {name}
                  {memberCreatorId && (
                    <MemberMark creatorId={memberCreatorId} userId={item?.userId} />
                  )}
                </p>
                {item?.userName && (
                  <p className="truncate text-xs text-slate-400 dark:text-slate-500">
                    @{maskEmailLike(item.userName)}
                  </p>
                )}
              </div>

              {/* Metric */}
              <div className="shrink-0 text-right">
                <p className="text-sm font-bold text-slate-800 dark:text-white">
                  {value}
                </p>
                <p className="text-[11px] text-slate-400 dark:text-slate-500">
                  {unit}
                </p>
              </div>
          </>
        );
        return (
          <li key={item.id ?? item.userId ?? index}>
            {item?.userName ? (
              <Link to={`/user/${item.userName}`} className={rowClassName}>
                {rowContent}
              </Link>
            ) : (
              <div className={rowClassName}>{rowContent}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default UkSlider;
